/**
 * Conversation history — what the model is allowed to remember between
 * requests.
 *
 * Two rules apply on the way out:
 *
 *  - Only the newest turn keeps its workbook snapshot. The loop mutates the
 *    workbook constantly, so older snapshots are confident descriptions of a
 *    spreadsheet that no longer exists; retaining them would undo the point of
 *    the per-iteration refresh. Conversational content is kept, the stale data
 *    goes.
 *  - History is bounded, so a long session cannot grow the request without
 *    limit.
 */

import {ChatMessage} from "./librechatApi";

/**
 * Trailing messages retained across a session. Counted in messages rather
 * than bytes because pruning already leaves at most one workbook snapshot,
 * so the rest of the history is small conversational text.
 */
export const MAX_HISTORY_MESSAGES = 20;

/** Stands in for a workbook snapshot once a newer one has superseded it. */
export const SNAPSHOT_PLACEHOLDER =
  "(omitted — superseded by the current data shown in the latest message)";

export interface TurnMessage extends ChatMessage {
  /**
   * The same message with the workbook snapshot replaced by a placeholder.
   * Set only on turns that carry a snapshot.
   */
  prunedContent?: string;
}

/**
 * Replaces the workbook snapshot with a placeholder on every turn that
 * carries one except the newest.
 */
export function pruneSnapshots(messages: TurnMessage[]): TurnMessage[] {
  let newest = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].prunedContent !== undefined) {
      newest = i;
      break;
    }
  }

  return messages.map((message, i) =>
    message.prunedContent !== undefined && i !== newest
      ? {...message, content: message.prunedContent}
      : message,
  );
}

/**
 * Replaces every workbook snapshot with the placeholder, the newest included.
 * Used when history is written to storage: a restored snapshot always
 * describes a workbook the user has since closed, so keeping it would feed
 * the model stale data on the next turn.
 */
export function pruneAllSnapshots(messages: TurnMessage[]): TurnMessage[] {
  return messages.map((message) =>
    message.prunedContent !== undefined
      ? {...message, content: message.prunedContent}
      : message,
  );
}

/**
 * Trims history to the most recent `max` messages, discarding any leading
 * assistant reply so the retained history still opens on a user message.
 */
/**
 * How many exchanges deep a new turn falls within its thread — 1 for the
 * first message, 2 for the next, and so on. Counted from prior user turns
 * rather than total message count so a follow-up's assistant reply and
 * action-result bookkeeping don't inflate the depth (issue #58).
 */
export function turnIndexOf(history: TurnMessage[] = []): number {
  return history.filter((message) => message.role === "user").length + 1;
}

export function capHistory(
  messages: TurnMessage[],
  max = MAX_HISTORY_MESSAGES,
): TurnMessage[] {
  if (messages.length <= max) return messages;

  let start = messages.length - max;
  while (start < messages.length && messages[start].role !== "user") {
    start++;
  }
  return messages.slice(start);
}

export interface HistoryCapResult {
  messages: TurnMessage[];
  truncated: boolean;
  originalCount: number;
}

/** Caps history while retaining the counts needed for degradation telemetry. */
export function capHistoryWithDiagnostics(
  messages: TurnMessage[],
  max = MAX_HISTORY_MESSAGES,
): HistoryCapResult {
  return {
    messages: capHistory(messages, max),
    truncated: messages.length > max,
    originalCount: messages.length,
  };
}

/** Drops the local pruning metadata so only the wire fields are sent. */
export function toChatMessages(messages: TurnMessage[]): ChatMessage[] {
  return messages.map(({role, content}) => ({role, content}));
}
